// src/context/auth/AuthContext.tsx
import React, { createContext, useReducer, useEffect } from 'react';
import { AuthState, AuthContextType, UserData } from './AuthTypes';
import { authService } from '../../api/authService';

const NO_ACTIVE_USER_MESSAGE = 'No active user found with the given email id.';

// Initial state for authentication
const initialState: AuthState = {
  isAuthenticated: false,
  userData: null,
  loading: true,
  error: null,
  sessionMessage: null,
};

// Define the action types
type AuthAction = 
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; userData: UserData }
  | { type: 'LOGIN_FAIL'; error: string }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_SESSION_MESSAGE'; message: string | null };

// Create the reducer function
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        loading: true,
        error: null
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        isAuthenticated: true,
        userData: action.userData,
        loading: false,
        error: null,
        sessionMessage: null,
      };
    case 'LOGIN_FAIL':
      return {
        ...state,
        isAuthenticated: false,
        userData: null,
        loading: false,
        error: action.error
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        userData: null,
        loading: false,
        sessionMessage: state.sessionMessage,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };
    case 'SET_SESSION_MESSAGE':
      return {
        ...state,
        sessionMessage: action.message,
      };
    default:
      return state;
  }
};

// Create the context with a default value
export const AuthContext = createContext<AuthContextType>({
  ...initialState,
  login: async () => {},
  logout: () => {},
  clearError: () => {},
  clearSessionMessage: () => {},
});

interface AuthProviderProps {
  children: React.ReactNode;
}

// Create the provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check for existing auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        dispatch({ type: 'LOGIN_START' });

        // Check if there's a token in localStorage
        const isAuth = authService.isAuthenticated();
        
        if (isAuth) {
          const userData = await authService.validateStoredSession();
          if (userData) {
            dispatch({ type: 'LOGIN_SUCCESS', userData });
          } else {
            authService.clearStoredAuth();
            dispatch({ type: 'LOGOUT' });
          }
        } else {
          dispatch({ type: 'LOGOUT' });
        }
        const reason = authService.consumeLogoutReason();
        if (reason) {
          dispatch({ type: 'SET_SESSION_MESSAGE', message: reason });
        }
      } catch (error) {
        console.error('Auth check error:', error);
        authService.clearStoredAuth();
        const message = error instanceof Error ? error.message : 'Authentication check failed';
        if (message === NO_ACTIVE_USER_MESSAGE) {
          dispatch({ type: 'LOGOUT' });
        } else {
          dispatch({
            type: 'LOGIN_FAIL',
            error: message,
          });
        }
      }
    };

    void checkAuth();
  }, []);

  // Login function
  const login = async (token: string) => {
    try {
      dispatch({ type: 'LOGIN_START' });
      
      const response = await authService.authenticateWithGoogle(token);
      
      if (response.success && response.userData) {
        dispatch({ type: 'LOGIN_SUCCESS', userData: response.userData });
      } else {
        dispatch({ 
          type: 'LOGIN_FAIL', 
          error: response.error || 'Authentication failed' 
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      dispatch({ 
        type: 'LOGIN_FAIL', 
        error: 'Authentication failed. Please try again.' 
      });
    }
  };

  // Logout function
  const logout = () => {
    authService.logout();
    dispatch({ type: 'LOGOUT' });
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const clearSessionMessage = () => {
    dispatch({ type: 'SET_SESSION_MESSAGE', message: null });
  };

  // Context value
  const contextValue: AuthContextType = {
    ...state,
    login,
    logout,
    clearError,
    clearSessionMessage,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}; 
